<script lang="ts">
  import { fly } from "svelte/transition";
  import toast from "svelte-french-toast";
  import { notifications } from "$lib/data";
  import { browser, dev } from "$app/environment";
  import {
    open_ws_connections,
    ws,
    ws_status,
  } from "$lib/websockets/connection";
  import { onDestroy, onMount } from "svelte";
  import Spinner from "$components/forms/Spinner.svelte";
  import { generate_notification_query } from "./notfication_data";
  import { page } from "$app/stores";
  import type { UserNotification } from "$types";

  interface WSNotificationMessage {
    type: "subscription" | "items";
    uid: string;
    data: UserNotification[];
    event?: "create" | "update" | "init";
  }

  export let size = 45;
  export let active = false;
  let width: number;
  let retryTimeout: ReturnType<typeof setTimeout>;
  let messageHandler: ((event: MessageEvent) => void) | null = null;

  $: uid = `notifications-${$page.data.user?.id}`;
  $: subscribed = $open_ws_connections.includes(uid);

  const collection = "user_notifications";

  function isValidNotificationMessage(
    data: any,
  ): data is WSNotificationMessage {
    return (
      data &&
      typeof data.type === "string" &&
      typeof data.uid === "string" &&
      Array.isArray(data.data)
    );
  }

  const handle_new_notification = (message: MessageEvent) => {
    try {
      if (!message?.data) return;

      const data = JSON.parse(message.data);
      if (!isValidNotificationMessage(data) || data.uid !== uid) return;

      if (data.type === "subscription") {
        handleSubscriptionEvent(data);
      } else if (data.type === "items" && Array.isArray(data.data)) {
        handleItemsEvent(data.data);
      }
    } catch (err) {
      console.error("Failed to handle notification:", err);
    }
  };

  function handleSubscriptionEvent(data: WSNotificationMessage) {
    switch (data.event) {
      case "create":
        if (dev) {
          toast.success("DEV TOAST: New message received!", {
            duration: 5000,
            position: "bottom-center",
          });
        }
        if (data.data.length > 0) {
          $notifications = [...$notifications, ...data.data];
        }
        break;

      case "update":
        if (dev) console.log("New update event", data.data);

        $notifications = $notifications.filter((n) => {
          const updatedItem = data.data.find((d) => d.id === n.id);
          if (!updatedItem) return true;
          return !updatedItem.seen_by.includes($page.data.user?.id);
        });
        break;

      case "init":
        if (dev) console.log("Successfully subscribed", data.data);

        if (data.data.length > 0) {
          $notifications = data.data;
        }
        $open_ws_connections = [...new Set([...$open_ws_connections, uid])];
        break;
    }
  }

  function handleItemsEvent(data: UserNotification[]) {
    if (dev) console.log("New items event", data);
    if (data?.length > 0) {
      $notifications = [...data, ...$notifications];
    }
  }

  const subscribe_to_notifications = async () => {
    if (!$page.data.user || $open_ws_connections.includes(uid)) return;

    if (dev) console.log("Subscribing to notifications");

    messageHandler = handle_new_notification;
    $ws?.addEventListener("message", messageHandler);

    let tries = 0;
    const maxTries = 20;
    const retryDelay = 3000;

    while (!$open_ws_connections.includes(uid) && tries < maxTries) {
      if (
        $ws_status === "connected" &&
        $page.data.user &&
        $ws?.readyState === WebSocket.OPEN
      ) {
        try {
          const query = generate_notification_query($page.data.user);
          $ws.send(
            JSON.stringify({
              type: "subscribe",
              collection,
              uid,
              query,
            }),
          );
        } catch (err) {
          console.error("Failed to send subscription:", err);
        }
      }

      await new Promise<void>((resolve) => {
        retryTimeout = setTimeout(resolve, retryDelay);
      });

      tries++;
    }

    if (tries === maxTries) {
      console.error("Failed to subscribe after maximum retries");
    }
  };

  onMount(() => {
    const initTimeout = setTimeout(() => {
      subscribe_to_notifications();
    }, 2000);

    return () => clearTimeout(initTimeout);
  });

  onDestroy(() => {
    clearTimeout(retryTimeout);

    if (browser && $page.data.user?.id && $ws_status === "connected") {
      try {
        if ($open_ws_connections.includes(uid)) {
          $ws?.send(
            JSON.stringify({
              type: "unsubscribe",
              collection,
              uid,
            }),
          );

          if (messageHandler) {
            $ws?.removeEventListener("message", messageHandler);
          }

          $notifications = [];
          $open_ws_connections = $open_ws_connections.filter((c) => c !== uid);

          if (dev) {
            console.log("Unsubscribed from notifications");
            console.log("Open connections:", $open_ws_connections);
          }
        }
      } catch (err) {
        console.error("Failed to unsubscribe:", err);
      }
    }
  });
</script>

<div
  class="notifications"
  class:active
  class:subscribed
  class:has_notifications={$notifications.length > 0}
  bind:clientWidth={width}
  style:border="{width * 0.05 > 4 ? width * 0.05 : 4}px solid white"
  style:width="{size}px"
>
  {#if subscribed}
    {#if $notifications.length > 0}
      <div
        transition:fly={{ duration: 200, x: -10, y: 10 }}
        class="notification_count"
        class:maxed={$notifications.length >= 5}
      >
        {$notifications.length >= 5 ? "5+" : $notifications.length}
      </div>
    {/if}
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
      <rect width="256" height="256" fill="none" />
      <path
        d="M221.8,175.94C216.25,166.38,208,139.33,208,104a80,80,0,1,0-160,0c0,35.34-8.26,62.38-13.81,71.94A16,16,0,0,0,48,200H88.81a40,40,0,0,0,78.38,0H208a16,16,0,0,0,13.8-24.06ZM128,216a24,24,0,0,1-22.62-16h45.24A24,24,0,0,1,128,216Z"
      />
    </svg>
  {:else}
    <div class="spinner">
      <Spinner tiny size_to_container />
    </div>
  {/if}
</div>

<style>
  .notification_count {
    position: absolute;
    top: -0.56rem;
    right: -0.56rem;
    background: var(--primary);
    color: white;
    border-radius: 0.9rem;
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
    font-weight: bold;
    z-index: 10;
    display: grid;
    place-content: center;
    border: 2px solid white;
    box-shadow: var(--shadow);
    transition: all 200ms ease;
  }

  .notification_count.maxed {
    right: -0.7rem;
  }

  .notifications {
    width: 100%;
    height: 100%;
    aspect-ratio: 1/1;
    position: relative;
    display: grid;
    place-content: center;
    background: var(--offDark);
    border-radius: 50%;
    font-weight: bold;
    color: white;
    line-height: 1;
    box-shadow: var(--shadow);
    z-index: 1;
    transition: all 0.2s ease;
  }

  .notifications .spinner {
    width: 1rem;
    height: 1rem;
  }

  .notifications:hover {
    border-color: var(--secondary) !important;
    cursor: pointer;
  }

  .notifications svg {
    width: 1.2rem;
    height: 1.2rem;
    color: white;
    fill: white;
  }

  .notifications.active {
    border-color: var(--secondary) !important;
  }

  .notifications.has_notifications {
    background: var(--secondary);
  }

  .notifications.has_notifications svg {
    animation: bellshake 11s infinite cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  }

  @keyframes bellshake {
    0% {
      transform: rotate(0);
    }
    1% {
      transform: rotate(5deg);
    }
    2% {
      transform: rotate(-5deg);
    }
    3% {
      transform: rotate(4deg);
    }
    4% {
      transform: rotate(-4deg);
    }
    5% {
      transform: rotate(2deg);
    }
    6% {
      transform: rotate(-2deg);
    }
    7% {
      transform: rotate(1deg);
    }
    8% {
      transform: rotate(0);
    }
    92% {
      transform: rotate(0);
    }
    95% {
      transform: rotate(1deg);
    }
  }
</style>
